import type { DiscoveryDistributionSupplierListResponseType } from "@sellar-io/api-v2/types";
import { ORDER_BUYER } from "../../../../constants/routes";
import { useEffect, useState } from "react";
import { useOrderLinkFlowDistributionSuppliers } from "../editAddSelection/hooks/useOrderLinkFlowDistributionSuppliers";
import { DiscoveryV2DistributionSupplierCard } from "../../../discovery/v2/distribution/DiscoveryV2DistributionSupplierCard";
import { useSupplierDefaultStorefrontLink } from "../../../../hooks/api/v2/storefrontLink/useSupplierDefaultStorefrontLink";

type DistributionSupplier =
  DiscoveryDistributionSupplierListResponseType[number];

interface DistributionSupplierBrowseListProps {
  buyerPostcode: string | null;
  excludeSupplierId: number | null;
}

export const DistributionSupplierBrowseList = ({
  buyerPostcode,
  excludeSupplierId,
}: DistributionSupplierBrowseListProps) => {
  const {
    suppliers,
    isLoading: distributionSuppliersListLoading,
    isError: distributionSuppliersListError,
  } = useOrderLinkFlowDistributionSuppliers({
    buyerPostcode,
    excludeSupplierId,
  });

  const [supplierLinkGenerating, setSupplierLinkGenerating] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (redirectUrl) {
      window.location.replace(redirectUrl);
    }
  }, [redirectUrl]);

  const {
    mutateAsync: supplierDefaultStorefrontLinkMutate,
    isError: supplierDefaultStorefrontLinkMutateError,
  } = useSupplierDefaultStorefrontLink();

  const handleSupplierClick = async (supplier: DistributionSupplier) => {
    setSupplierLinkGenerating(true);
    try {
      const { defaultStorefrontLink } =
        await supplierDefaultStorefrontLinkMutate(supplier.urlId);

      const storefrontLinkPassword =
        defaultStorefrontLink?.password && defaultStorefrontLink.includePricing
          ? defaultStorefrontLink.password
          : null;

      const queryParams = new URLSearchParams();
      if (buyerPostcode) {
        queryParams.set("postcode", buyerPostcode);
      }
      if (storefrontLinkPassword) {
        queryParams.set("storefrontLink", storefrontLinkPassword);
      }

      const baseUrl = ORDER_BUYER.href({
        supplierIdOrUrlId: supplier.urlId,
      });
      const url = queryParams.toString()
        ? `${baseUrl}?${queryParams.toString()}`
        : baseUrl;

      setRedirectUrl(url);
    } catch {
      setSupplierLinkGenerating(false);
    }
  };

  const isLoading = distributionSuppliersListLoading || supplierLinkGenerating;

  return (
    <>
      {isLoading && <SupplierGridSkeleton />}

      {distributionSuppliersListError && (
        <div className="rounded-xl border border-zinc-200 bg-white p-6 text-center">
          <p className="text-sm text-zinc-600">
            Something went wrong loading suppliers. Please try again later.
          </p>
        </div>
      )}
      {supplierDefaultStorefrontLinkMutateError && (
        <div className="rounded-xl border border-zinc-200 bg-white p-6 text-center">
          <p className="text-sm text-zinc-600">
            Something went generating supplier storefront link. Please try again
            later.
          </p>
        </div>
      )}

      {!isLoading &&
        !distributionSuppliersListError &&
        suppliers.length === 0 && (
          <div className="rounded-xl border border-zinc-200 bg-white p-6 text-center">
            <p className="text-sm text-zinc-600">
              No additional suppliers are available for your area at the moment.
            </p>
          </div>
        )}

      {!isLoading &&
        !distributionSuppliersListError &&
        suppliers.length > 0 && (
          <div className="grid grid-cols-2 gap-3">
            {suppliers.map((supplier) => (
              <DiscoveryV2DistributionSupplierCard
                key={supplier.id}
                imageUrl={supplier.imageUrl ?? ""}
                logoUrl={supplier.Business.logoUrl ?? ""}
                businessName={supplier.Business.name}
                deliveryAddressCity={supplier.Business.DeliveryAddress.city}
                className="w-full min-w-0"
                onClick={() => handleSupplierClick(supplier)}
              />
            ))}
          </div>
        )}
    </>
  );
};

const SupplierGridSkeleton = () => {
  return (
    <div className="grid grid-cols-2 gap-3">
      {Array.from({ length: 6 }).map((_, index) => (
        <div
          key={index}
          className="animate-pulse rounded-2xl bg-white drop-shadow-md"
        >
          <div className="h-30 rounded-t-2xl bg-zinc-200" />
          <div className="flex gap-2 p-2">
            <div className="h-8 w-8 shrink-0 rounded-full bg-zinc-200" />
            <div className="flex flex-1 flex-col justify-center gap-1">
              <div className="h-3.5 w-24 rounded bg-zinc-200" />
              <div className="h-3 w-16 rounded bg-zinc-200" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
